import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  InputGroup,
  InputRightElement,
  Select,
  Spinner,
  Text,
  Textarea,
  useToast,
  VStack,
  Badge,
  SimpleGrid,
  Checkbox,
  IconButton,
  useClipboard,
  Alert,
  AlertIcon,
  AlertTitle, 
  AlertDescription,
  Center,
  Stack,
  useDisclosure,
  Tooltip,
  InputLeftElement,
  Wrap, 
  WrapItem,
  Tag,
  TagLabel,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Divider,
  Link,
} from '@chakra-ui/react';

import { CheckIcon, MinusIcon, InfoIcon } from '@chakra-ui/icons';
import { getRegistryPublic, getRegistryAdmin, updateRegistry as updateRegistryData, deleteRegistry, saveMealSignup, getMealSignupsByRegistry, subscribeToNotifications } from '../services/RegistryService';
import { shareOnFacebook, shareViaEmail, shareViaText } from '../utils/sharingUtils';
import * as SharingUtils from '../utils/sharingUtils';
import { authenticateAdmin, requestPasswordReset, requestPasswordResetByEmail } from '../services/AuthService';
import { v4 as uuidv4 } from 'uuid';
import { mealTags, usStates } from '../utils/constants';// Auth states
import MealPreferencesDialog from '../components/MealPreferencesDialog';
import { SessionWarningDialog } from '../components/SessionWarningDialog';
import { useAutoLogout } from '../services/useAutoLogout';
import MealScheduleCustomizationDialog from '../components/MealScheduleCustomizationDialog';
import { foodSubscriptionService, FoodSubscription } from '../services/FoodSubscriptionService';

enum AuthState {
  LOADING = 'loading',
  UNAUTHENTICATED = 'unauthenticated',
  MEMBER = 'member',
  ADMIN = 'admin',
  ERROR = 'error'
}

interface RegistryViewProps {
  viewType?: 'admin' | 'member';
}

export type mealDays = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday' | '';
export type mealPlan = 'breakfast' | 'lunch' | 'dinner' | 'snack' | '';
export type MealType = 'meatless' | 'meatIncluded' | 'custom' | 'restaurant' | 'homemade';

export interface MealDetails {
  mealName: string;
}

const defaultMealDetails: MealDetails = {
  mealName: ''
};

const RegistryView = ({ viewType }: RegistryViewProps) => {
  const { registryId } = useParams<{ registryId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const location = useLocation();
  const autoAuthenticated = location.state?.autoAuthenticated;
  
  // State
  const [registry, setRegistry] = useState<any>(null);
  const [authState, setAuthState] = useState<AuthState>(AuthState.LOADING);
  const [error, setError] = useState<string>('');
  const [adminPassword, setAdminPassword] = useState('');
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [showPasswords, setShowPasswords] = useState(false);
  const adminButtonRef = useRef<HTMLDivElement>(null);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  
  // Add these state variables in the RegistryView component after the other state declarations
  const [notificationEmail, setNotificationEmail] = useState('');
  const [notificationPhone, setNotificationPhone] = useState('');
  // Add these state variables after the existing state declarations (around line 85)
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [isRequestingReset, setIsRequestingReset] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [showNotificationSuccess, setShowNotificationSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');

  const [showSessionWarning, setShowSessionWarning] = useState(false);
  
  // Load registry data - use public fetch initially
  useEffect(() => {
    const fetchRegistry = async () => {
      if (!registryId) {
        setError('Invalid registry ID');
        setAuthState(AuthState.ERROR);
        return;
      }
      
      try {
        // Use public fetch that only returns non-sensitive data
        const data = await getRegistryPublic(registryId);
        if (data) {
          setRegistry(data);
          
          // If this is auto-authenticated admin, we need to fetch full data
          if (autoAuthenticated) {
            const adminData = await getRegistryAdmin(registryId);
            if (adminData) {
              setRegistry(adminData);
              setAuthState(AuthState.ADMIN);
            } else {
              setAuthState(AuthState.ERROR);
              setError('Failed to load admin data');
            }
          } else if (viewType === 'member') {
            setAuthState(AuthState.MEMBER);
          } else {
            setAuthState(AuthState.UNAUTHENTICATED);
          }
        } else {
          setError('MommaFuel not found');
          setAuthState(AuthState.ERROR);
        }
      } catch (err) {
        console.error('Error fetching registry:', err);
        setError('Failed to load registry');
        setAuthState(AuthState.ERROR);
      }
    };
    
    fetchRegistry();
  }, [registryId, viewType, autoAuthenticated]);

  const handleSubscribe = () => {
    if (!notificationEmail && !notificationPhone) {
      toast({
        title: 'Missing Information',
        description: 'Please provide either an email or phone number to receive notifications',
        status: 'warning',
        duration: 3000,
        isClosable: true
      });
      return;
    }

    if (!registryId) {
      toast({
        title: 'Error',
        description: 'Invalid registry ID',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
      return;
    }

    try {
      // Now registryId is guaranteed to be a string
      subscribeToNotifications(registryId, notificationEmail || '', notificationPhone || '');
      
      setRegisteredEmail(notificationEmail);
      setShowNotificationSuccess(true);
      
      // Reset the form
      setNotificationEmail('');
      setNotificationPhone('');
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to subscribe to notifications',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
    }
  };
  
  // Handle click outside for admin login popup
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (adminButtonRef.current && !adminButtonRef.current.contains(event.target as Node)) {
        setShowAdminLogin(false);
      }
    }
    
    // Add event listener when the admin login popup is open
    if (showAdminLogin) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    // Clean up the event listener
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAdminLogin]);
  
  // Handle owner login - fetch full admin data after authentication
  const handleAdminLogin = async () => {
    if (!registry) return;
    
    try {
      setIsLoggingIn(true);
      
      const isAuthenticated = await authenticateAdmin(registry.registryId, adminPassword);
      
      if (isAuthenticated) {
        // Fetch full admin data after successful authentication
        const adminData = await getRegistryAdmin(registry.registryId);
        if (adminData) {
          setRegistry(adminData); // Replace registry with full admin data
          setShowAdminLogin(false);
          setAuthState(AuthState.ADMIN);
        } else {
          throw new Error('Failed to load admin data');
        }
      } else {
        toast({
          title: 'Error',
          description: 'Invalid owner password',
          status: 'error',
          duration: 3000,
        });
      }
    } catch (error) {
      console.error('Authentication error:', error);
      toast({
        title: 'Error',
        description: 'There was a problem authenticating. Please try again.',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!resetEmail.trim()) {
      toast({
        title: 'Missing Email',
        description: 'Please enter your email address',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    if (!SharingUtils.validateEmail(resetEmail)) {
      toast({
        title: 'Invalid Email',
        description: 'Please enter a valid email address',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    setIsRequestingReset(true);
    
    try {
      // For security, we always show success even if email doesn't exist
      toast({
        title: 'Reset Email Sent',
        description: '',
        status: 'success',
        duration: 5000,
      });
      
      // Call the API but don't show different messages based on result
      const success = await requestPasswordResetByEmail(resetEmail);
      // Reset the forgot password form
      setShowForgotPassword(false);
      setResetEmail('');
      
    } catch (error) {
      console.error('Password reset request error:', error);
      // Still show success message for security
      toast({
        title: 'Reset Email Sent',
        description: '',
        status: 'success',
        duration: 5000,
      });
      setShowForgotPassword(false);
      setResetEmail('');
    } finally {
      setIsRequestingReset(false);
    }
  };
  
  // Handle logout
  const handleLogout = () => {
    setAuthState(AuthState.UNAUTHENTICATED);
    setAdminPassword('');
    navigate(`/registry/${registryId}`);
    setShowSessionWarning(false); // Close warning dialog if open
  };

  const handleSessionWarning = useCallback(() => {
    setShowSessionWarning(true);
  }, []);

  // Auto-logout hook
  const { extendSession } = useAutoLogout({
    isAuthenticated: authState === AuthState.ADMIN || authState === AuthState.MEMBER,
    onLogout: handleLogout,
    timeoutMinutes: 15,
    warningMinutes: 2,
    onWarning: handleSessionWarning
  });

  // Render based on auth state and viewType
  const renderContent = () => {    

    // Main registry page (no viewType specified) or member view
    switch (authState) {
      case AuthState.LOADING:
        return (
          <Center py={10}>
            <VStack spacing={4}>
              <Spinner size="xl" />
              <Text>Loading MommaFuel...</Text>
            </VStack>
          </Center>
        );
        
      case AuthState.ERROR:
        return (
          <Alert
            status="error"
            variant="subtle"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            height="200px"
            borderRadius="md"
          >
            <AlertIcon boxSize="40px" mr={0} />
            <AlertTitle mt={4} mb={1} fontSize="lg">
              Error Loading MommaFuel
            </AlertTitle>
            <AlertDescription maxWidth="sm">
              {error || 'An unknown error occurred'}
            </AlertDescription>
            <Button mt={4} colorScheme="blue" onClick={() => navigate('/')}>
              Return Home
            </Button>
          </Alert>
        );
        
      case AuthState.UNAUTHENTICATED:
        return (
          <Box 
            borderWidth="1px" 
            borderRadius="lg" 
            p={{ base: 3, md: 5 }} 
            bg="white" 
            shadow="md" 
            position="relative"
            maxW={{ base: "100%", md: "800px" }}
            mx="auto"
            overflow="visible"
            height="auto" 
            // minHeight="340px"
            marginBottom="290px" 
          >
            
            {/* Main Content - Focused on joining the registry */}
            <VStack spacing={4} pt={4}>
              <Heading size="lg" textAlign="center">
                {registry?.parentNames}'s MommaFuel
              </Heading>
              {/* Wrap button and popup in a relative container */}
              <Box position="relative" display="inline-block">
                {/* Admin Access Button */}
                <Button
                  variant="outline"
                  size="sm"
                  colorScheme="green"
                  onClick={() => setShowAdminLogin(!showAdminLogin)}
                >
                  Owner Access
                </Button>

                {/* Admin Login Tooltip */}
                {showAdminLogin && (
                  <Box
                    position="absolute"
                    top="100%"
                    left="50%"
                    transform="translateX(-50%)"
                    p={3}
                    mt={2}
                    bg="white"
                    borderWidth="1px"
                    borderRadius="md"
                    shadow="md"
                    zIndex={10}
                    width="300px"

                  >
                    {/* Rest of the tooltip content remains the same */}
                    <VStack spacing={3} align="stretch">
                      <Flex justify="space-between" align="center" mb={1}>
                        <Text fontWeight="bold">Owner Login</Text>
                        <IconButton 
                          aria-label="Close"
                          icon={<MinusIcon />}
                          size="xs"
                          variant="ghost"
                          onClick={() => setShowAdminLogin(false)}
                        />
                      </Flex>
                      
                      <FormControl>
                        <Input 
                          type={showPasswords ? 'text' : 'password'} 
                          value={adminPassword} 
                          onChange={(e) => setAdminPassword(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && adminPassword) {
                              handleAdminLogin();
                            }
                          }}
                          placeholder="Password"
                        />
                      </FormControl>
                      <Checkbox 
                        isChecked={showPasswords}
                        alignSelf="flex-start"
                        onChange={(e) => setShowPasswords(e.target.checked)}
                        colorScheme="green"
                      >
                        Show Password
                      </Checkbox>
                      <Button 
                        colorScheme="green" 
                        width="full"
                        onClick={handleAdminLogin}
                        _hover={{}}
                        isDisabled={!adminPassword || isLoggingIn}
                        isLoading={isLoggingIn}
                        loadingText="Logging in"
                        size="sm"
                      >
                        Login as Owner
                      </Button>
                      {!showForgotPassword ? (
                        <Button
                          variant="outline"
                          size="sm"
                          colorScheme="green"
                          onClick={() => setShowForgotPassword(true)}
                        >
                          Forgot Password?
                        </Button>
                      ) : (
                        <VStack spacing={3} align="stretch" width="100%">
                          <Text fontSize="sm" fontWeight="bold">Reset Password</Text>
                          <Input
                            placeholder="Enter MommaFuel email address"
                            value={resetEmail}
                            onChange={(e) => {
                              setResetEmail(e.target.value);
                              setEmailError(''); // Clear error when user types
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && resetEmail) {
                                handleForgotPassword();
                              }
                            }}
                            type="email"
                            isInvalid={!!emailError}
                          />
                          {emailError && (
                            <Text fontSize="xs" color="red.500">
                              {emailError}
                            </Text>
                          )}
                          <HStack justify="center" spacing={3} width="100%">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setShowForgotPassword(false);
                                setResetEmail('');
                                setEmailError('');
                              }}
                            >
                              Cancel
                            </Button>
                            <Button
                              colorScheme="green"
                              size="sm"
                              onClick={handleForgotPassword}
                              isLoading={isRequestingReset}
                              loadingText="Sending..."
                              isDisabled={!resetEmail.trim()}
                            >
                              Send Reset Email
                            </Button>
                          </HStack>
                        </VStack>
                      )}
                    </VStack>
                  </Box>
                )}
              </Box>
              
              {registry.status === 'PENDING' && (
                <Text fontSize="lg" maxWidth="400px" textAlign="center">
                    You'll be able to sign up for meals once the baby arrives, and the parents activate their MommaFuel.
                </Text>
              )}

              {/* {registry.status === 'ACTIVE' && (
                <Text fontSize="lg" textAlign="center" mt={8}>
                  This registry is active. You can now sign up for meals.
                </Text>
              )} */}
              
              <Button 
                size="lg"
                fontSize="xl"
                mt={0}
                hidden={registry.status !== 'ACTIVE'}
                onClick={() => {
                  setAuthState(AuthState.MEMBER);
                }}
                isDisabled={registry.status !== 'ACTIVE'}
              >
                {'Bring a Meal'}
              </Button>
              
              {registry.status !== 'ACTIVE' && (
                <Box>
                <Heading size="md" mb={3} mt={3} color="mamaFuel.text.primary">Sign up to be notified when meal slots open</Heading>
                <VStack align="stretch" spacing={4}>
                    <Stack direction={{ base: "column", md: "row" }} spacing={4} width="100%">
                    <FormControl>
                      <FormLabel>Email</FormLabel>
                    <Input 
                        type="email" 
                        value={notificationEmail}
                        onChange={(e) => setNotificationEmail(e.target.value)}
                      />
                      </FormControl>
                      {/* <FormControl>
                      <FormLabel>Phone Number</FormLabel>
                      <Input 
                        type="tel" 
                        value={notificationPhone}
                        onChange={(e) => setNotificationPhone(e.target.value)}
                        onKeyDown={SharingUtils.onlyAllowDigits}
                        minLength={10}
                        maxLength={10}
                      />
                      </FormControl> */}
                    </Stack>
                    <Button
                      isDisabled={
                        !notificationEmail
                      }
                      _hover={{}}
                      colorScheme="blue"
                      width="50%"
                      size="lg"
                      alignSelf="center"
                      fontSize="lg"
                      onClick={handleSubscribe}
                    >
                      Get Notified
                    </Button>
                </VStack>
              </Box>
              )}
            </VStack>
          </Box>
        );
        
      case AuthState.MEMBER:
        return <MemberView 
          registry={registry} 
          onLogout={handleLogout} 
          navigate={navigate} 
          onUpdateRegistry={(updatedRegistry) => setRegistry(updatedRegistry)}
        />;
        
      case AuthState.ADMIN:
        return <AdminView 
          registry={registry} 
          onLogout={handleLogout} 
          navigate={navigate} 
          updateRegistry={(updatedRegistry) => setRegistry(updatedRegistry)} 
        />;
        
      default:
        return null;
    }
  };
  
  return (
    <Box 
      className="registry-view-container"
      bg="mamaFuel.cream" 
      width="100%" 
      height="auto"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      overflowY="auto"
      overflowX="hidden"
      pb="2"
      pt="2"
    >
      {/* Content */}
      <Container 
        maxW="container.xl" 
        width="100%"
        py="2"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
      >
        {renderContent()}
      </Container>

      <SessionWarningDialog
        isOpen={showSessionWarning}
        onClose={() => setShowSessionWarning(false)}
        onExtendSession={() => {
          extendSession();
          setShowSessionWarning(false);
        }}
        onLogout={handleLogout}
        warningTimeSeconds={120} // 2 minutes warning
      />

      <Modal isOpen={showNotificationSuccess} onClose={() => setShowNotificationSuccess(false)} isCentered>
        <ModalOverlay bg="rgba(0, 0, 0, 0.6)" />
        <ModalContent mx={4} maxW="md">
          <ModalHeader>
            <HStack spacing={3}>
              <CheckIcon color="green.500" boxSize={6} />
              <Text>Sign Up Successful!</Text>
            </HStack>
          </ModalHeader>
          
          <ModalBody pb={6}>
            <VStack spacing={4} align="stretch">
              <Text>
                Perfect! We've registered your email for activation notifications.
              </Text>
              
              <Box 
                p={4} 
                bg="green.50" 
                borderRadius="md" 
                borderWidth="1px" 
                borderColor="green.200"
              >
                <Text fontWeight="bold" color="green.700" fontSize="lg" textAlign="center">
                  {registeredEmail}
                </Text>
              </Box>
              
              <Text fontSize="sm" color="gray.600" textAlign="center">
              You'll receive an email notification as soon as {registry?.parentNames ? `${registry.parentNames}'s` : "the parent's"} MommaFuel becomes active and meal slots are available for signups.
              </Text>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button 
              colorScheme="green" 
              onClick={() => setShowNotificationSuccess(false)}
              width="100%"
            >
              Got it!
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

// Member view component - displays registry information and meal signup
const MemberView = ({ registry, onLogout, navigate, onUpdateRegistry }: { 
  registry: any, 
  onLogout: () => void, 
  navigate: (path: string) => void,
  onUpdateRegistry?: (updatedRegistry: any) => void 
}) => {
  // Remove the MealType type and simplify the state management
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [deliveryWindow, setDeliveryWindow] = useState({
    start: registry.defaultTimeWindow?.start || '17:00',
    end: registry.defaultTimeWindow?.end || '19:00'
  });
  // Use a more flexible type to avoid TypeScript errors
  const [mealDetails, setMealDetails] = useState<MealDetails>(defaultMealDetails);
  const [availableDates, setAvailableDates] = useState<string[]>([]);
  const [signedUpMeals, setSignedUpMeals] = useState<any[]>([]);
  const toast = useToast();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationStep, setConfirmationStep] = useState<'contact' | 'sponsor-info' | 'summary'>('contact');
  const [isGiftersChoice, setIsGiftersChoice] = useState(false);
  const [isBringSomethingElse, setIsBringSomethingElse] = useState(false);
  const [selectedSponsoredRestaurant, setSelectedSponsoredRestaurant] = useState<any>(null);
  const [contactInfo, setContactInfo] = useState({
    name: '',
    email: '',
    phone: ''
  });
  // Replace selectedSponsoredRestaurant with selectedFoodSubscription
  const [selectedFoodSubscription, setSelectedFoodSubscription] = useState<FoodSubscription | null>(null);
  const [foodSubscriptions, setFoodSubscriptions] = useState<FoodSubscription[]>([]);
  const [loadingFoodSubscriptions, setLoadingFoodSubscriptions] = useState(false);
  
  // Generate meal delivery dates based on first delivery date and meal day
  useEffect(() => {
    if (registry.status === 'ACTIVE' && registry.currentSignUps) {
      // Get all dates from currentSignUps (both claimed and unclaimed)
      const allDates = registry.currentSignUps.map((signup: any) => signup.date);
      setAvailableDates(allDates);
      
      // Extract signed up meals from claimed signups
      if (registry.currentSignUps && Array.isArray(registry.currentSignUps)) {
        const claimedMeals = registry.currentSignUps
          .filter((signup: any) => signup.isClaimed)
          .map((signup: any) => ({
            date: signup.date,
            name: signup.gifterName,
            mealName: signup.actualMealName || signup.name,
            timeWindow: signup.timeWindow,
            mealType: signup.type
          }));
          
        setSignedUpMeals(claimedMeals);
      }
    }
  }, [registry]);

  // Refresh the meal slots list when a new signup is added 
  // Find this useEffect around line 1177 and update it:
  useEffect(() => {
    if (registry.registryId && successMessage) {
      const refreshMealSlots = async () => {
        try {
          // First, get the updated registry data
          const updatedRegistry = await getRegistryPublic(registry.registryId);
          
          if (updatedRegistry) {
            // Update the parent component's registry state
            if (onUpdateRegistry) {
              onUpdateRegistry(updatedRegistry);
            }
            
            // Extract signed up meals from the updated registry
            if (updatedRegistry.currentSignUps && Array.isArray(updatedRegistry.currentSignUps)) {
              const claimedMeals = updatedRegistry.currentSignUps
                .filter((signup: any) => signup.isClaimed)
                .map((signup: any) => ({
                  date: signup.date,
                  name: signup.gifterName,
                  mealName: signup.actualMealName || signup.name,
                  timeWindow: signup.timeWindow || updatedRegistry.defaultTimeWindow,
                  mealType: signup.type
                }));
                
              setSignedUpMeals(claimedMeals);
            }
          }
          
          // Fallback to just getting the signups if the registry doesn't have the data
          const signups = await getMealSignupsByRegistry(registry.registryId);
          if (signups && signups.length > 0) {
            setSignedUpMeals(signups.map((signup: any) => ({
              date: signup.date,
              name: signup.gifterName,
              mealName: signup.mealName,
              timeWindow: signup.timeWindow || registry.defaultTimeWindow,
              mealType: signup.mealType
            })));
          }
        } catch (error) {
          console.error('Error refreshing meal signups:', error);
        }
      };
      
      // Only refresh if we have signedUpMeals or if the registry is active
      if (signedUpMeals.length > 0 || registry.status === 'ACTIVE') {
        refreshMealSlots();
      }
    }
  }, [successMessage]); // REMOVED: registry.registryId, registry.status, onUpdateRegistry

  // Add this at the top of your component with other refs/state
  const isFetchingSubscriptions = useRef(false);

  // Update the useEffect to use only specific properties and remove problematic dependencies
  useEffect(() => {
    const fetchFoodSubscriptions = async () => {
      console.log('Food subscriptions fetch check:', {
        hasRegistry: !!registry,
        registryStatus: registry?.status,
        hasZip: !!registry?.promotionalZipCode,
        zip: registry?.promotionalZipCode,
        currentSubscriptionsCount: foodSubscriptions.length,
        isFetching: isFetchingSubscriptions.current
      });

      // Only fetch if:
      // 1. Registry exists
      // 2. Registry has promotional zip code
      // 3. Food subscriptions haven't been loaded yet
      // 4. Not currently fetching
      if (!registry?.promotionalZipCode || 
          foodSubscriptions.length > 0 ||
          isFetchingSubscriptions.current) {
        return;
      }

      console.log('Fetching food subscriptions for promotional zip:', registry.promotionalZipCode);
      isFetchingSubscriptions.current = true;
      setLoadingFoodSubscriptions(true);
      
      try {
        const subscriptions = await foodSubscriptionService.getFoodSubscriptionsByZipCode(registry.promotionalZipCode);
        console.log('Fetched subscriptions:', subscriptions);
        setFoodSubscriptions(subscriptions);
      } catch (error) {
        console.error('Error fetching food subscriptions:', error);
        setFoodSubscriptions([]);
        toast({
          title: 'Info',
          description: 'Unable to load restaurant deals for this area',
          status: 'info',
          duration: 3000,
        });
      } finally {
        setLoadingFoodSubscriptions(false);
        isFetchingSubscriptions.current = false;
      }
    };

    fetchFoodSubscriptions();
  }, [
    registry?.promotionalZipCode, // Only the specific property we need
    foodSubscriptions.length,
    // Removed: loadingFoodSubscriptions, toast, registry
    toast
  ]);

  // Handle meal signup
  const handleSignUp = async () => {
    if (!selectedDate || !mealDetails.mealName) {
      toast({
        title: 'Missing information',
        description: 'Please select a date and provide meal details',
        status: 'warning',
        duration: 3000,
      });
      return;
    }
    
    // Check if the date is already taken 
    if (isDateTaken(selectedDate)) {
      toast({
        title: 'Date already taken',
        description: 'Someone has already signed up for this date. Please select a different date.',
        status: 'error',
        duration: 3000,
      });
      return;
    }
    
    // Show confirmation dialog
    setConfirmationStep('contact');
    setShowConfirmation(true);
  };

  const formatPhoneNumber = (value: string) => {
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, '');
    
    // Limit to 10 digits
    const limitedDigits = digits.slice(0, 10);
    
    // Format as XXX-XXX-XXXX
    if (limitedDigits.length <= 3) {
      return limitedDigits;
    } else if (limitedDigits.length <= 6) {
      return `${limitedDigits.slice(0, 3)}-${limitedDigits.slice(3)}`;
    } else {
      return `${limitedDigits.slice(0, 3)}-${limitedDigits.slice(3, 6)}-${limitedDigits.slice(6)}`;
    }
  };
  
  // Add this helper function near the other helper functions in MemberView
  const getTimeWindowForDate = (date: string) => {
    const currentSignup = registry.currentSignUps?.find((signup: any) => signup.date === date);
    return currentSignup?.timeWindow || registry.defaultTimeWindow;
  };
  
  // Actual submission after confirmation
  const confirmSignUp = async () => {
    // Validate contact info
    if (!contactInfo.name || !contactInfo.email || !contactInfo.phone) {
      toast({
        title: 'Missing information',
        description: 'Please provide your name, email, and phone number',
        status: 'warning',
        duration: 3000,
      });
      return;
    }
    
    // Double check the date isn't taken (in case someone else signed up while dialog was open)
    if (isDateTaken(selectedDate)) {
      setShowConfirmation(false);
      toast({
        title: 'Date already taken',
        description: 'Someone has already signed up for this date while you were filling out the form. Please select a different date.',
        status: 'error',
        duration: 5000,
      });
      return;
    }
    
    // Find the original meal preference to get the correct type
    let mealType: 'restaurant' | 'homemade' = 'homemade';
    if (!isGiftersChoice && !isBringSomethingElse) {
      // Try to find the selected meal in the preferences to get its type
      mealType = 'homemade'; // Default to homemade
    }
    
    setLoading(true);
    setError('');
    setSuccessMessage('');
    
    try {
      // Create the signup data for updating the meal slot
      const signupData: {
        registryId: string;
        date: string;
        name: string;
        email: string;
        phone: string;
        mealName: string;
        mealType: 'restaurant' | 'homemade';
        timeWindow: {
          start: string;
          end: string;
        };
        gifterId: string;
        selectedFoodSubscription?: FoodSubscription | null;
      } = {
        registryId: registry.registryId,
        date: selectedDate,
        name: contactInfo.name,
        email: contactInfo.email,
        phone: contactInfo.phone,
        mealName: isGiftersChoice 
          ? `Gifter's Choice: ${mealDetails.mealName}` 
          : isBringSomethingElse 
            ? `Custom: ${mealDetails.mealName}` 
            : mealDetails.mealName,
        timeWindow: deliveryWindow,
        mealType: mealType,
        gifterId: uuidv4(),
        selectedFoodSubscription: selectedFoodSubscription
      };
      
      // Call the API to save the meal signup
      const response = await saveMealSignup(signupData);
      
      // Store signup data including coupon code for success page
      sessionStorage.setItem('signupData', JSON.stringify({
        registry: registry,
        mealDetails: mealDetails,
        selectedDate: selectedDate,
        contactInfo: contactInfo,
        selectedFoodSubscription: selectedFoodSubscription,
        couponCode: response.couponCode || null
      }));
      
      navigate(`/registry/${registry.registryId}/signup-success`);
      // Close confirmation dialog
      setShowConfirmation(false);
    } catch (err: any) {
      console.error('Error signing up for meal:', err);
      
      // Provide a more specific error message if the slot is already filled
      if (err.message && err.message.includes('already taken')) {
        setError('This date has already been taken. Please choose another date.');
        toast({
          title: 'Date already taken',
          description: 'Someone has already signed up for this date. Please select a different date.',
          status: 'error',
          duration: 3000,
        });
        setShowConfirmation(false);
        setSelectedDate('');
        getRegistryPublic(registry.registryId);
      } else {
        setError('Failed to sign up for meal');
        toast({
          title: 'Error',
          description: 'Failed to sign up for meal',
          status: 'error',
          duration: 3000,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    
    // If it's an ISO string from a Date object, split out just the date part
    const datePart = dateString.includes('T') ? dateString.split('T')[0] : dateString;
    
    // Parse the date string properly to avoid timezone conversion
    const [year, month, day] = datePart.split('-').map(Number);
    
    // Create a Date object with fixed time (noon) in UTC
    const date = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
    
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };
  
  // Format time for display
  const formatTime = (time: string) => {
    if (!time) return '';
    
    const [hours, minutes] = time.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  };
  
  // Check if a date is already taken
  const isDateTaken = (date: string) => {
    if (!registry.currentSignUps) return false;
    const signup = registry.currentSignUps.find((s: any) => s.date === date);
    return signup ? signup.isClaimed : false;
  };
  
  // Get the meal for a specific date
  const getMealForDate = (date: string) => {
    if (!registry.currentSignUps) return null;
    const signup = registry.currentSignUps.find((s: any) => s.date === date && s.isClaimed);
    return signup ? {
      name: signup.gifterName,
      mealName: signup.actualMealName || signup.name,
      timeWindow: signup.timeWindow,
      mealType: signup.type
    } : null;
  };
  
  // Get registry status text
  const handleContinue = () => {
    // Validate email and phone
    const isEmailValid = SharingUtils.validateEmail(contactInfo.email);
    const isPhoneValid = contactInfo.phone && contactInfo.phone.length >= 12;
    
    // Only proceed if both are valid
    if (isEmailValid && isPhoneValid) {
      // If there's a selected food subscription with actual promotions, show sponsor info first
      if (selectedFoodSubscription && 
          selectedFoodSubscription.promotion && 
          selectedFoodSubscription.promotion.length > 0) {
        setConfirmationStep('sponsor-info');
      } else {
        // Skip sponsor info for non-promotional food subscriptions
        setConfirmationStep('summary');
      }
    } else if (!isEmailValid) {
      toast({
        title: 'Invalid Email',
        description: 'Please enter a valid email address',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } else if (!isPhoneValid) {
      toast({
        title: 'Invalid Phone Number',
        description: 'Please enter a valid phone number',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };
  
  // Regular active registry view
  return (
    <Box borderWidth="1px" borderRadius="lg" bg="white" shadow="md">
      
      {/* Signup Section - only shown if status is ACTIVE */}
      {registry.status === 'ACTIVE' && (
        <Box p={6} borderBottomWidth="1px">
          <Heading size="sm" mb={4}>Meal Calendar</Heading>
          
          <SimpleGrid columns={{ base: 1, md: selectedDate ? 2 : 1 }} spacing={6}>
            {/* Calendar View */}
            <Box width="100%" maxHeight="55vh" overflowY="auto" mx={selectedDate ? "0" : "auto"} maxW={selectedDate ? "100%" : "600px"} transition="all 0.3s ease">
              <Heading size="xs" mb={3} color="gray.500">Choose a Date</Heading>
              
            {availableDates.map((date, index) => {
              const meal = getMealForDate(date);
              const isTaken = !!meal;
              
              // Find the signup object for this date to get the specific time window
              const currentSignup = registry.currentSignUps?.find((signup: any) => signup.date === date);
              const timeWindow = currentSignup?.timeWindow || registry.defaultTimeWindow;
              
              // Check if the date is in the past
              // Create dates for UTC-safe comparison
              const [yearStr, monthStr, dayStr] = date.split('-').map(Number);
              const dateObj = new Date(Date.UTC(yearStr, monthStr - 1, dayStr, 12, 0, 0));
              
              // Get today's date at UTC midnight
              const now = new Date();
              const today = new Date(Date.UTC(
                now.getFullYear(),
                now.getMonth(),
                now.getDate(),
                0, 0, 0
              ));
              
              // Check if date is strictly before today (not including today)
              const isPastDate = dateObj < today;
              
              // Determine if this date is available for selection
              const isSelectable = !isTaken && !isPastDate;
              
              return (
                <Box 
                  key={index} 
                  p={3} 
                  mt={2}
                  borderWidth="1px" 
                  borderRadius="md" 
                  bg={isTaken ? 'gray.50' : isPastDate ? 'gray.50' : 'white'}
                  cursor={isSelectable ? 'pointer' : 'default'}
                  onClick={() => isSelectable && setSelectedDate(date)}
                  opacity={isSelectable ? 1 : 0.7}
                  _hover={isSelectable ? { bg: 'blue.50' } : {}}
                  transition="all 0.2s"
                >
                  <HStack justify="space-between">
                    <VStack align="start" spacing={0}>
                      <Text fontWeight="bold">
                        {formatDate(date)}
                      </Text>
                      {isTaken ? (
                        <VStack align="start" spacing={0} mt={1}>
                          <Text fontSize="sm">
                            <strong>{meal.name}</strong> is bringing <strong>{
                              meal.mealName.startsWith('Gifter\'s Choice:') 
                                ? meal.mealName.substring('Gifter\'s Choice:'.length).trim() 
                                : meal.mealName.startsWith('Custom:') 
                                  ? meal.mealName.substring('Custom:'.length).trim()
                                  : meal.mealName
                            }</strong>
                          </Text>
                        </VStack>
                      ) : (
                        <HStack mt={1} spacing={2}>
                          {isPastDate ? (
                            <Badge colorScheme="grey">Date Passed</Badge>
                          ) : (
                            <Badge colorScheme="green">Available</Badge>
                          )}
                          {isSelectable && selectedDate === date && (
                            <Badge colorScheme="blue">Selected</Badge>
                          )}
                        </HStack>
                      )}
                      <Text fontSize="xs" color="gray.600">
                        Meal drop off between {formatTime(timeWindow?.start)} - {formatTime(timeWindow?.end)}
                      </Text>
                    </VStack>
                  </HStack>
                </Box>
              );
            })}
            </Box>
            
            {/* Signup Form - Only show when a date is selected */}
            {selectedDate && (
              <Box>
                <Heading size="xs" mb={3} color="gray.500">Choose a Meal</Heading>
                
                <VStack spacing={4} align="stretch" p={4} borderWidth="1px" borderRadius="md">
                  
                  {/* Display Meal Tags */}
                  {registry.mealTags && registry.mealTags.length > 0 && (
                    <Box>
                      <Text fontWeight="bold" mb={3}>Meal suggestions for inspiration:</Text>
                      <Wrap spacing={2}>
                        {/* Show mealTags */}
                        {registry.mealTags.map((tag: any, index: number) => {
                          const tagInfo = mealTags.find(mealTag => mealTag.name === tag.name);
                          const icon = tagInfo?.icon || "🏷️";
                          
                          return (
                            <WrapItem key={`meal-tag-${index}`}>
                              <Tag
                                size="md"
                                bg="gray.100"
                                color="gray.700"
                                borderRadius="full"
                                px={3}
                                py={1}
                              >
                                <TagLabel>
                                  {icon} {tag.name}
                                </TagLabel>
                              </Tag>
                            </WrapItem>
                          );
                        })}
                      </Wrap>
                    </Box>
                  )}
                  
                  {/* Meal Input */}
                  <Box>
                    <FormControl >
                      <FormLabel fontWeight="bold">What will you bring?</FormLabel>
                      <Input 
                        value={mealDetails.mealName}
                        onChange={(e) => {
                          const newValue = e.target.value;
                          setMealDetails(prev => ({ ...prev, mealName: newValue }));
                          
                          // Check if the typed text matches any restaurant name (case insensitive)
                          const matchingSubscription = foodSubscriptions.find(sponsor => 
                            sponsor.name.toLowerCase() === newValue.toLowerCase()
                          );
                          
                          if (matchingSubscription) {
                            // If we found a match, select it
                            setSelectedFoodSubscription(matchingSubscription);
                          } else {
                            // If no match, clear any existing selection
                            setSelectedFoodSubscription(null);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleSignUp();
                            
                          }
                        }}
                        size="md"
                      />
                    </FormControl>
                  </Box>

                  <Divider />

                  {foodSubscriptions.length > 0 && (
                    <Box>
                      <Text fontWeight="bold" mb={3}>Or choose from sponsored restaurants!</Text>
                      {loadingFoodSubscriptions ? (
                        <Center p={4}>
                          <Spinner size="md" />
                          <Text ml={3}>Loading restaurant deals...</Text>
                        </Center>
                      ) : (
                        <SimpleGrid columns={{ base: 1, md: 1 }} spacing={3}>
                          {foodSubscriptions.map((sponsor: FoodSubscription, index: number) => (
                            <Box
                              key={sponsor.id || index}
                              p={3}
                              borderWidth="2px"
                              borderRadius="md"
                              borderColor={selectedFoodSubscription?.id === sponsor.id ? "blue.500" : "gray.200"}
                              bg={selectedFoodSubscription?.id === sponsor.id ? "blue.50" : "white"}
                              cursor="pointer"
                              transition="all 0.2s"
                              _hover={{
                                borderColor: selectedFoodSubscription?.id === sponsor.id ? "blue.600" : "gray.300",
                                transform: "translateY(-1px)",
                                boxShadow: "md"
                              }}
                              _active={{
                                transform: "translateY(0px)",
                              }}
                              onClick={() => {
                                setSelectedFoodSubscription(
                                  selectedFoodSubscription?.id === sponsor.id ? null : sponsor
                                );
                                // Update meal details when selecting a food sponsor
                                if (selectedFoodSubscription?.id !== sponsor.id) {
                                  setMealDetails(prev => ({ 
                                    ...prev, 
                                    mealName: `${sponsor.name}` 
                                  }));
                                } else {
                                  setMealDetails(prev => ({ 
                                    ...prev, 
                                    mealName: '' 
                                  }));
                                }
                              }}
                            >
                              <VStack align="start" spacing={2}>
                                <Text fontSize="sm" fontWeight="bold">
                                  {sponsor.name}
                                </Text>
                                {sponsor.promotion && sponsor.promotion.length > 0 && (
                                  <VStack align="start" spacing={1}>
                                    {sponsor.promotion.map((promo: string, promoIndex: number) => (
                                      <Box
                                        key={promoIndex}
                                        bg="red.300"
                                        color="black"
                                        px={3}
                                        py={1}
                                        borderRadius="md"
                                        fontSize="sm"
                                        fontWeight="bold"
                                        boxShadow="sm"
                                        border="2px solid black"
                                      >
                                        🏷️ {promo}
                                      </Box>
                                    ))}
                                  </VStack>
                                )}
                                {sponsor.address && (
                                  <Text fontSize="xs" color="gray.600">
                                    📍 {sponsor.address.street}, {sponsor.address.city}
                                  </Text>
                                )}
                                {(sponsor.webLink || sponsor.phoneNumber) && (
                                  <HStack spacing={4}>
                                    {sponsor.webLink && sponsor.webLink.length > 0 && (
                                      <Link
                                        href={sponsor.webLink}
                                        isExternal
                                        fontSize="xs"
                                        color="blue.600"
                                        textDecoration="underline"
                                        _hover={{ color: "blue.800" }}
                                      >
                                        🔗 {sponsor.name}
                                      </Link>
                                    )}
                                    {sponsor.phoneNumber && (
                                      <Text fontSize="xs" color="gray.600">
                                        📞 {sponsor.phoneNumber}
                                      </Text>
                                    )}
                                  </HStack>
                                )}
                              </VStack>
                            </Box>
                          ))}
                        </SimpleGrid>
                      )}
                    </Box>
                  )}
                </VStack>
              </Box>
            )}
          </SimpleGrid>
          
          {/* Confirmation Dialog */}
          {showConfirmation && (
            <Box
              position="fixed"
              top="0"
              left="0"
              right="0"
              bottom="0"
              bg="rgba(0,0,0,0.7)"
              zIndex="1000"
              display="flex"
              alignItems="center"
              justifyContent="center"
              p={4}
            >
              <Box
                bg="white"
                p={4}
                borderRadius="md"
                maxW="2xl"
                width="400px"
              >
                <Heading size="md" mb={4}>
                  {confirmationStep === 'contact' ? 'Meal Signup Information' : 
                   confirmationStep === 'sponsor-info' ? 'Sponsored Deal Information' :
                   'Confirm Meal Signup'}
                </Heading>
                
                {confirmationStep === 'contact' ? (
                  <>
                    {/* Contact information form */}
                    <VStack spacing={3} mb={4} align="stretch">
                      <FormControl isRequired>
                        <FormLabel>Your Name</FormLabel>
                        <Input 
                          value={contactInfo.name}
                          onChange={(e) => setContactInfo(prev => ({ ...prev, name: e.target.value }))}
                        />
                      </FormControl>
                      
                      <FormControl isRequired>
                        <FormLabel>Email Address</FormLabel>
                        <Input 
                          type="email"
                          value={contactInfo.email}
                          onChange={(e) => setContactInfo(prev => ({ ...prev, email: e.target.value }))}
                        />
                      </FormControl>
                      
                      <FormControl isRequired>
                        <FormLabel>Phone Number</FormLabel>
                        <InputGroup>
                        <InputLeftElement 
                          pointerEvents="none" 
                          color="gray.500" 
                          fontSize="sm"
                          pl={2}
                        >
                          +1
                        </InputLeftElement>
                        <Input 
                          value={contactInfo.phone}
                          onChange={(e) => {
                            const formatted = formatPhoneNumber(e.target.value);
                            setContactInfo(prev => ({ ...prev, phone: formatted }));
                          }}
                          onKeyDown={(e) => {
                            // First handle digit restriction
                            SharingUtils.onlyAllowDigits(e);
                            // Then check for Enter key press if the event hasn't been prevented
                            if (!e.defaultPrevented && e.key === 'Enter') {
                              handleContinue();
                            }
                          }}
                          minLength={10}
                          pl={10} 
                        />
                        </InputGroup>
                      </FormControl>
                    </VStack>
                    
                    <Flex justify="space-between">
                      <Button 
                        variant="outline" 
                        onClick={() => {
                          setConfirmationStep('contact');
                          setShowConfirmation(false);
                        }}
                      >
                        Cancel
                      </Button>
                      <Button 
                        colorScheme="blue" 
                        onClick={handleContinue}
                      >
                        Continue
                      </Button>
                    </Flex>
                  </>
                ) : confirmationStep === 'sponsor-info' ? (
                  <>
                    {/* Sponsor deal information step */}
                    <VStack spacing={4} align="stretch">
                      <Text fontSize="lg" fontWeight="bold" textAlign="center">
                        {selectedFoodSubscription?.name}
                      </Text>
                      
                        <Box
                          bg="red.300"
                          color="black"
                          px={3}
                          py={2}
                          borderRadius="md"
                          fontSize="md"
                          fontWeight="bold"
                          boxShadow="sm"
                          border="2px solid black"
                          width="100%"
                        >
                          🏷️ {selectedFoodSubscription?.promotion?.[0] || 'No promotion available'}
                        </Box>

                        <Text fontSize="md" textAlign="center">
                          {selectedFoodSubscription?.claimInstructions}
                        </Text>
                    </VStack>
                    
                    <Flex justify="space-between" mt={6}>
                      <Button 
                        variant="outline" 
                        onClick={() => setConfirmationStep(
                          (selectedFoodSubscription && 
                           selectedFoodSubscription.promotion && 
                           selectedFoodSubscription.promotion.length > 0) 
                            ? 'sponsor-info' 
                            : 'contact'
                        )}
                      >
                        Back
                      </Button>
                      <Button 
                        colorScheme="blue" 
                        onClick={() => setConfirmationStep('summary')}
                      >
                        Continue to Summary
                      </Button>
                    </Flex>
                  </>
                ) : (
                  <>
                    {/* Summary step - existing code */}
                    {registry.additionalNotes && (
                      <Text >From {registry.parentNames}: {registry.additionalNotes}</Text>
                    )}
                    <Box p={4} borderRadius="md" mb={6}>
                      <Text>
                        "I will bring <strong>{
                              mealDetails.mealName
                        }</strong> for <strong>{
                          (registry.numberOfAdults && registry.numberOfKids && registry.numberOfKids > 0)
                            ? `${registry.numberOfAdults} adults and ${registry.numberOfKids} ${registry.numberOfKids === 1 ? 'child' : 'children'}`
                            : (registry.numberOfAdults ? `${registry.numberOfAdults} adults` : `${registry.numberOfPeople || 2} people`)
                        }</strong>{
                          // Only show address if it exists
                          registry.address?.street ? (
                            <> to <strong>{registry.address?.street}, {registry.address?.city}, {registry.address?.state} {registry.address?.zip}</strong></>
                          ) : (
                            <> </>
                          )
                        } between <strong>{formatTime(getTimeWindowForDate(selectedDate)?.start)} - {formatTime(getTimeWindowForDate(selectedDate)?.end)}</strong> on <strong>{formatDate(selectedDate)}</strong>"
                      </Text>
                    </Box>
                    <Flex justify="space-between">
                      <Button 
                        variant="outline" 
                        onClick={() => setConfirmationStep(selectedFoodSubscription && selectedFoodSubscription.promotion && selectedFoodSubscription.promotion.length > 0 ? 'sponsor-info' : 'contact')}
                      >
                        Back
                      </Button>
                      <Button 
                        colorScheme="green" 
                        onClick={confirmSignUp}
                        isLoading={loading}
                        loadingText="Submitting..."
                      >
                        Submit
                      </Button>
                    </Flex>
                  </>
                )}
              </Box>
            </Box>
          )}
        </Box>
      )}

            {/* MommaFuel Status */}
            <Box p={6} borderBottomWidth="1px">
        <Box>
          <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4}>
            <Box>
            <Text fontWeight="bold" fontSize="sm" mb={1}>Foods we don't like:</Text>
            {registry.dislikes && registry.dislikes.length > 0 ? (
              <Text fontSize="sm">{registry.dislikes.join(', ')}</Text>
            ) : (
              <Text fontSize="sm">--</Text>
            )}
          </Box>
          
          <Box>
            <Text fontWeight="bold" fontSize="sm" mb={1}>Dietary Restrictions:</Text>
            <Text fontSize="sm">{registry.dietaryRestrictions || "--"}</Text>
          </Box>
          <Box>
            <Text fontWeight="bold" fontSize="sm" mb={1}>People eating:</Text>
            <Text fontSize="sm">
              {registry.numberOfAdults || 2} adults
              {(registry.numberOfKids && registry.numberOfKids > 0) 
                ? ` and ${registry.numberOfKids} ${registry.numberOfKids === 1 ? 'child' : 'children'}`
                : ''}
            </Text>
          </Box>
          <Box>
            <Text fontWeight="bold" fontSize="sm" mb={1}>Drop Off Address:</Text>
            <Text fontSize="sm">
              {registry.address?.street ? (
                `${registry.address?.street}${registry.address?.street2 ? `, ${registry.address?.street2}` : ''}, ${registry.address?.city}, ${registry.address?.state} ${registry.address?.zip}`
              ) : (
                "--"
              )}
            </Text>
          </Box>
          </SimpleGrid>
        </Box>
      </Box>
      
      {/* Additional Notes & Contact */}
        {/* Sign Up Button - Moved to bottom of form */}
          <Box mt={registry.additionalNotes ? 6 : 0} mb={4} textAlign="center">
            <Button 
              colorScheme="green" 
              size="lg"
              onClick={handleSignUp}
              isLoading={loading}
              loadingText="Signing Up"
              isDisabled={!selectedDate || !mealDetails.mealName}
              bg="mamaFuel.moss.700"
              color="white"
              _hover={{ bg: "mamaFuel.moss.600" }}
              mt={4}
            >
              Sign Up to Bring This Meal
            </Button>
          </Box>
          <Button onClick={onLogout} variant="outline" size="sm" mb={4}>
            Back to MommaFuel Home
          </Button>
    </Box>
  );
};

// Admin view component - allows editing registry and updating baby status
const AdminView = ({ 
  registry, 
  onLogout, 
  navigate, 
  updateRegistry 
}: { 
  registry: any, 
  onLogout: () => void, 
  navigate: (path: string) => void,
  updateRegistry: (updatedRegistry: any) => void
}) => {
  const [formData, setFormData] = useState({
    ...registry,
    babyStatus: registry.status || 'PENDING',
    firstDeliveryDate: registry.firstDeliveryDate || '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showAddMealDates, setShowAddMealDates] = useState(false);
  const [additionalDays, setAdditionalDays] = useState(1);
  const [noAddressProvided, setNoAddressProvided] = useState(false);
  const { isOpen: isNoAddressDialogOpen, onOpen: onNoAddressDialogOpen, onClose: onNoAddressDialogClose } = useDisclosure();
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  const toast = useToast();
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showShareOptions, setShowShareOptions] = useState(false);
  const shareButtonRef = useRef<HTMLDivElement>(null);
  const [isMealPreferencesOpen, setIsMealPreferencesOpen] = useState(false);
  const [isMealScheduleOpen, setIsMealScheduleOpen] = useState(false);
  
  // Initialize noAddressProvided based on registry data
  useEffect(() => {
    if (registry) {
      // Check if address fields are empty - if so, assume no address was provided
      const hasNoAddress = !registry.address?.street && !registry.address?.city && 
                           !registry.address?.state && !registry.address?.zip;
      setNoAddressProvided(hasNoAddress);
      
      // ... existing useEffect code ...
    }
  }, [registry]);

  // Add handlers for no address functionality
  const handleNoAddressChange = (checked: boolean) => {
    if (checked) {
      // Show confirmation dialog
      onNoAddressDialogOpen();
    } else {
      // Unchecking - reset to normal state
      setNoAddressProvided(false);
    }
  };

  const handleConfirmNoAddress = () => {
    setNoAddressProvided(true);
    // Clear address fields when user confirms no address
    setFormData((prev: typeof formData) => ({
      ...prev,
      address: {
        street: '',
        street2: '',
        city: '',
        state: '',
        zip: ''
      },
      addressVisibilityAcknowledged: false
    }));
    onNoAddressDialogClose();
  };

  const handleCancelNoAddress = () => {
    setNoAddressProvided(false);
    onNoAddressDialogClose();
  };

  // Update validation to handle optional address
  const isFormValid = () => {
    // Basic information
    if (!formData.parentName1 || 
        !formData.dueDate || 
        !formData.email || 
        !formData.phoneNumber) {
      toast({
        title: 'Missing information',
        description: 'Please fill in all required family information fields',
        status: 'warning',
        duration: 3000,
      });
      return false;
    }
    
    // MommaFuel coordinator information
    if (!formData.adminContact?.name || 
        !formData.adminContact?.relationship || 
        !formData.adminContact?.email || 
        !formData.adminContact?.phone) {
      toast({
        title: 'Missing information',
        description: 'Please fill in all required registry coordinator fields',
        status: 'warning',
        duration: 3000,
      });
      return false;
    }

    // Address information - only validate if address is being provided
    if (!noAddressProvided) {
      if (!formData.address?.street || 
          !formData.address?.city || 
          !formData.address?.state || 
          !formData.address?.zip) {
        toast({
          title: 'Missing information',
          description: 'Please fill in all required address fields',
          status: 'warning',
          duration: 3000,
        });
        return false;
      }

      if (!formData.addressVisibilityAcknowledged) {
        toast({
          title: 'Missing acknowledgment',
          description: 'Please acknowledge that the delivery address will be visible to anyone with the link',
          status: 'warning',
          duration: 3000,
        });
        return false;
      }
    }
    
    // Meal drop off preferences
    if (!formData.defaultTimeWindow?.start || 
        !formData.defaultTimeWindow?.end ||
        formData.defaultTimeWindow.start >= formData.defaultTimeWindow.end) {
      toast({
        title: 'Invalid delivery window',
        description: 'Please set a valid delivery time window (end time must be after start time)',
        status: 'warning',
        duration: 3000,
      });
      return false;
    }
    
    return true;
  };

  // Prevent form submission when Enter key is pressed in input fields
  const preventFormSubmit = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && e.target instanceof HTMLInputElement) {
      e.preventDefault();
    }
  };
  

  // Add this at the top level of the AdminView component, after other useState declarations
  // Get today's date in YYYY-MM-DD format for min date attribute
  const today = new Date().toISOString().split('T')[0];
  
  // Calculate max date (1 year from today)
  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() + 1);

  // Add click outside handler for share tooltip
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (shareButtonRef.current && !shareButtonRef.current.contains(event.target as Node)) {
        setShowShareOptions(false);
      }
    }
    
    // Add event listener when the share options are open
    if (showShareOptions) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    // Clean up the event listener
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showShareOptions]);
  
  // Format time for display
  const formatTime = (time: string) => {
    if (!time) return '';
    
    const [hours, minutes] = time.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  const handleAddMoreMealDates = () => {
    setShowAddMealDates(true);
  };

  const handleSaveAdditionalDays = async () => {
    const newTotalDays = (formData.mealDays || 0) + additionalDays;
    
    setFormData((prev: typeof formData) => ({
      ...prev,
      mealDays: newTotalDays
    }));

    formData.mealDays = newTotalDays;

    // Reset the add meal dates UI
    setShowAddMealDates(false);
    setAdditionalDays(1);

    // Save the changes immediately without exiting
    await handleSubmit(null as unknown as React.FormEvent<Element>, false);
    
  };

  const handleCancelAddMealDates = () => {
    setShowAddMealDates(false);
    setAdditionalDays(1);
  };

  // Process and sort current sign-ups
  const sortCurrentSignUps = (signUps: any[]) => {
    if (!signUps || !Array.isArray(signUps)) return [];
    
    // Sort by date (soonest first)
    return signUps.sort((a, b) => {
      const dateA = a.date;
      const dateB = b.date;
      if (!dateA || !dateB) return 0;
      return new Date(dateA).getTime() - new Date(dateB).getTime();
    });
  };
  
  // Initialize form data with sorted preferences and sign-ups
  useEffect(() => {
    if (registry) {
      // Use the new structure directly
      const mealTags = registry.mealTags || [];
      const currentSignUps = registry.currentSignUps || [];
      
      const sortedSignUps = sortCurrentSignUps(currentSignUps);
      
      // Automatically set addressVisibilityAcknowledged to true if there's a street address
      const hasStreetAddress = registry.address?.street && registry.address.street.trim().length > 0;
      
      setFormData({
        ...registry,
        babyStatus: registry.status || 'PENDING',
        firstDeliveryDate: registry.firstDeliveryDate || '',
        mealTags: mealTags,
        currentSignUps: sortedSignUps,
        addressVisibilityAcknowledged: registry.addressVisibilityAcknowledged ?? (hasStreetAddress ? true : false)
      });
    }
  }, [registry]);
  
  // Update parentNames whenever parentName1 or parentName2 changes
  useEffect(() => {
    if (formData.parentName1) {
      let combined = formData.parentName1;
      if (formData.parentName2) {
        combined += ` & ${formData.parentName2}`;
      }
      
      if (combined !== formData.parentNames) {
        setFormData((prev: typeof formData) => ({
          ...prev,
          parentNames: combined
        }));
      }
    }
  }, [formData.parentName1, formData.parentName2]);
  
  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev: typeof formData) => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle nested object changes (like address fields)
  const handleNestedChange = (parent: string, field: string, value: string) => {
    setFormData((prev: typeof formData) => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: value
      }
    }));
  };

  // Handle form submission to update registry
  const handleSubmit = async (e: React.FormEvent, logout: boolean = true) => {
    e?.preventDefault();
    
    // Validate the form before submitting
    if (!isFormValid()) {
      return;
    }
    
    setIsSubmitting(true);
    setError('');
    setSuccessMessage('');
    
    try {
      // Ensure sign-ups are properly sorted before saving
      const sortedSignUps = sortCurrentSignUps(formData.currentSignUps || []);
      
      // Create a copy of the form data with sorted sign-ups
      const dataToSave = {
        ...formData,
        mealTags: formData.mealTags || [],
        currentSignUps: sortedSignUps
      };

      // Call the API to update the registry
      await updateRegistryData(registry.registryId, dataToSave);
      
      // Refresh registry data after update
      const updatedRegistry = await getRegistryAdmin(registry.registryId);
      if (updatedRegistry) {
        // Update both the registry state and the formData state
        setFormData((prev: typeof formData) => ({
          ...prev,
          ...updatedRegistry,
          babyStatus: updatedRegistry.status || 'PENDING',
          mealTags: updatedRegistry.mealTags || prev.mealTags || [],
          currentSignUps: sortCurrentSignUps(updatedRegistry.currentSignUps || [])
        }));
        
        // Call the parent component's updateRegistry function prop
        updateRegistry(updatedRegistry);
      }
      
      toast({
        title: 'Success',
        description: 'MommaFuel has been updated',
        status: 'success',
        duration: 3000,
      });
      
      // Return to home view by calling the logout function
      // This resets to UNAUTHENTICATED state instead of navigating
      if (logout) {
        onLogout();
      }
      
    } catch (err) {
      console.error('Error updating registry:', err);
      setError('Failed to update registry');
      toast({
        title: 'Error',
        description: 'Failed to update registry',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Handle registry deletion
  const handleDeleteRegistry = async () => {
    setIsDeleting(true);
    
    try {
      await deleteRegistry(registry.registryId);
      toast({
        title: 'MommaFuel Deleted',
        description: 'The registry has been permanently deleted',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      
      // Navigate to home after successful deletion
      navigate('/');
    } catch (err) {
      console.error('Error deleting registry:', err);
      toast({
        title: 'Error',
        description: 'Failed to delete registry. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };
  
  // Format date for display
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    
    // If it's an ISO string from a Date object, split out just the date part
    const datePart = dateString.includes('T') ? dateString.split('T')[0] : dateString;
    
    // Parse the date string properly to avoid timezone conversion
    const [year, month, day] = datePart.split('-').map(Number);
    
    // Create a Date object with fixed time (noon) in UTC
    const date = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
    
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };
  
  // Generate meal delivery dates from currentSignUps
  const getDeliveryDates = () => {
    if (!formData.currentSignUps || !Array.isArray(formData.currentSignUps)) return [];
    
    // Get all dates from currentSignUps and sort them
    const dates = formData.currentSignUps
      .map((signup: any) => {
        const [year, month, day] = signup.date.split('-').map(Number);
        return new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
      })
      .sort((a: Date, b: Date) => a.getTime() - b.getTime()); // Sort by date

    return dates;
  };
  // Check if status is active
  const isStatusActive = registry.status === 'ACTIVE';
  
  // Add new states and hooks
  const registryUrl = `${window.location.origin}/registry/${registry.registryId}`;
  const { hasCopied, onCopy } = useClipboard(registryUrl);
  
  // Add share handlers
  const handleShareOnFacebook = () => shareOnFacebook(registryUrl);
  const handleShareViaEmail = () => shareViaEmail(registryUrl, registry?.parentNames);
  const handleShareViaText = () => shareViaText(registryUrl);

  // Convert currentSignUps to MealDate format for the dialog
  const getCurrentMealSchedule = () => {
    if (!formData.currentSignUps || !Array.isArray(formData.currentSignUps)) return [];
    
    return formData.currentSignUps.map((signup: any) => ({
      date: signup.date,
      timeWindow: signup.timeWindow || formData.defaultTimeWindow || { start: '17:00', end: '19:00' },
      isClaimed: signup.isClaimed
    }));
  };

  // Get dates that are claimed and should be read-only
  const getClaimedDates = () => {
    if (!formData.currentSignUps || !Array.isArray(formData.currentSignUps)) return [];
    
    return formData.currentSignUps
      .filter((signup: any) => signup.isClaimed)
      .map((signup: any) => signup.date);
  };

  // Handle saving the updated meal schedule
  const handleSaveMealSchedule = async (customDates: any[]) => {
    try {
      // Convert the custom dates back to currentSignUps format
      const updatedSignUps = customDates.map(mealDate => {
        // Find existing signup to preserve claimed information
        const existingSignup = formData.currentSignUps?.find((signup: any) => signup.date === mealDate.date);
        
        if (existingSignup) {
          // Update existing signup with new time window (but only if not claimed)
          return {
            ...existingSignup,
            timeWindow: existingSignup.isClaimed ? existingSignup.timeWindow : mealDate.timeWindow
          };
        } else {
          // Create new unclaimed signup
          return {
            date: mealDate.date,
            timeWindow: mealDate.timeWindow,
            isClaimed: false,
            name: null,
            gifterName: null,
            actualMealName: null,
            type: null
          };
        }
      });

      // Create the updated formData
      const updatedFormData = {
        ...formData,
        currentSignUps: sortCurrentSignUps(updatedSignUps)
      };

      // Only update local state after successful save
      setFormData(updatedFormData);

      // Save to backend first
      await updateRegistryData(registry.registryId, updatedFormData);
      
      toast({
        title: 'Success',
        description: 'Meal schedule has been updated',
        status: 'success',
        duration: 3000,
      });
      
    } catch (error) {
      console.error('Error updating meal schedule:', error);
      toast({
        title: 'Error',
        description: 'Failed to update meal schedule',
        status: 'error',
        duration: 3000,
      });
    }
  };

  return (
    <Box as="form" onSubmit={handleSubmit} onKeyDown={preventFormSubmit} borderWidth="1px" borderRadius="lg" bg="white" shadow="md" maxW="100%" overflow="hidden">
      <Box p={{ base: 4, md: 6 }} borderBottomWidth="1px">
        {/* Responsive header layout */}
        <Stack 
          spacing={4} 
          direction={{ base: "column", md: "row" }}
          justify={{ base: "center", md: "space-between" }}
          align={{ base: "center", md: "center" }}
        >
          <Box position="relative" ref={shareButtonRef} order={{ base: 2, md: 1 }}>
            <Button
              colorScheme="green"
              onClick={() => setShowShareOptions(!showShareOptions)}
              width={{ base: "100%", md: "auto" }}
              maxW={{ base: "200px", md: "none" }}
            >
              Share MommaFuel
            </Button>
            {showShareOptions && (
              <Box
                position="absolute"
                top="100%"
                left="50%"
                transform="translateX(-40%)"
                mt={2}
                p={3}
                bg="white"
                borderWidth="1px"
                borderRadius="md"
                shadow="md"
                zIndex={10}
                minW="200px"
                maxW="90vw"
              >
                <VStack spacing={2} align="stretch">
                  <Flex justify="space-between" align="center" mb={1}>
                    <Text fontWeight="bold" fontSize="sm">Share MommaFuel</Text>
                    <IconButton 
                      aria-label="Close"
                      icon={<MinusIcon />}
                      size="xs"
                      variant="ghost"
                      onClick={() => setShowShareOptions(false)}
                    />
                  </Flex>
                  <InputGroup size="sm" mb={2}>
                    <Input
                      pr="4.5rem"
                      type="text"
                      value={registryUrl}
                      isReadOnly
                      fontSize="xs"
                    />
                    <InputRightElement width="4.5rem">
                      <Button h="1.5rem" size="xs" onClick={onCopy}>
                        {hasCopied ? "Copied" : "Copy"}
                      </Button>
                    </InputRightElement>
                  </InputGroup>
                  <Button
                    colorScheme="facebook"
                    onClick={handleShareOnFacebook}
                    size="sm"
                    mb={1}
                    height="auto"
                    py={2}
                    whiteSpace="normal"
                    textAlign="center"
                  >
                    Facebook
                  </Button>
                  <Button
                    colorScheme="blue"
                    onClick={handleShareViaEmail}
                    size="sm"
                    mb={1}
                  >
                    Email
                  </Button>
                  <Button
                    colorScheme="green"
                    onClick={handleShareViaText}
                    size="sm"
                  >
                    Text
                  </Button>
                </VStack>
              </Box>
            )}
          </Box>

          <Text 
            fontSize="lg" 
            color="black" 
            textAlign="center" 
            wordBreak="break-word"
            order={{ base: 1, md: 2 }}
            flex={{ base: "none", md: "1" }}
          >
            MommaFuel for: {registry?.parentNames}
          </Text>

          <Button
            colorScheme="blue"
            type="submit"
            isLoading={isSubmitting}
            loadingText="Saving Changes"
            order={{ base: 3, md: 3 }}
            width={{ base: "100%", md: "auto" }}
            maxW={{ base: "200px", md: "none" }}
          >
            Save Changes and Exit
          </Button>
        </Stack>
      </Box>
      
      {/* Baby Status Section */}
      <Box p={6} borderBottomWidth="1px" bg="blue.50">
        <VStack spacing={4} align="stretch">
          <Box>
            {isStatusActive ? (
                <>
                  <Heading size="sm" mb={2} fontWeight="bold">MommaFuel Status: Active</Heading>
                  <Text>MommaFuel is active! Members can now sign up to bring meals.</Text>
                </>
            ) : (
              <>
                <Heading size="sm" mb={2} fontWeight="bold">MommaFuel Status: Pending Baby Birth</Heading>
                <Text>Meal signups are not active yet. When the baby is born, you can activate the MommaFuel and choose the first meal date.</Text>
              </>
            )}
          </Box>
          
          <HStack justify="space-between" spacing={6} align="flex-start" flexDirection={{ base: "column", md: "row" }}>
            {isStatusActive && (
              // Show meal schedule when active
              <Box flex="1" mb={4}>
                <HStack justify="space-between" align="center" mb={3}>
                <Text fontWeight="bold">Meal Schedule</Text>
                  <Button
                    size="sm"
                    colorScheme="blue"
                    onClick={() => setIsMealScheduleOpen(true)}
                  >
                    Customize Schedule
                  </Button>
                </HStack>
                
                <Box borderWidth="1px" borderRadius="md" bg="blue.50">
                  {formData.firstDeliveryDate ? (
                    <VStack align="stretch" spacing={3}>
                      {/* Get all dates from the delivery schedule and sort them */}
                      {getDeliveryDates().map((date: Date, index: number) => {
                        const dateStr = date.toISOString().split('T')[0];
                        // Find if this date has a claimed meal in currentSignUps
                        let claimedMeal: any = null;
                        if (formData.currentSignUps && Array.isArray(formData.currentSignUps)) {
                          claimedMeal = formData.currentSignUps.find((signup: any) => 
                            signup.isClaimed && signup.date === dateStr
                          );
                        }
                        
                        if (claimedMeal) {
                          // This date has a claimed meal - show the details
                          return (
                            <Box key={`date-${index}`} p={2} borderWidth="1px" borderRadius="md" bg="white">
                              <HStack justify="space-between">
                                <VStack align="start" spacing={0}>
                                  <Text fontWeight="bold">
                                    Day {index + 1}: {formatDate(dateStr)}
                                  </Text>
                                  <Text fontSize="sm">
                                    <strong>{claimedMeal.gifterName}</strong> is bringing <strong>{
                                      claimedMeal.actualMealName ? (
                                        claimedMeal.actualMealName.startsWith('Gifter\'s Choice:') 
                                          ? claimedMeal.actualMealName.substring('Gifter\'s Choice:'.length).trim() 
                                          : claimedMeal.actualMealName.startsWith('Custom:') 
                                            ? claimedMeal.actualMealName.substring('Custom:'.length).trim()
                                            : claimedMeal.actualMealName
                                      ) : claimedMeal.name
                                    }</strong>
                                  </Text>
                                  <Text fontSize="xs" color="gray.600">
                                    Meal drop off between {formatTime(claimedMeal.timeWindow?.start || formData.defaultTimeWindow?.start)} - {formatTime(claimedMeal.timeWindow?.end || formData.defaultTimeWindow?.end)}
                                  </Text>
                                </VStack>
                                <Badge colorScheme={claimedMeal.type === 'restaurant' ? 'blue' : 'green'}>
                                  Signed up
                                </Badge>
                              </HStack>
                            </Box>
                          );
                        } else {
                          // This date is available
                          // Find the signup object to get the time window
                          const unclaimedSignup = formData.currentSignUps?.find((signup: any) => signup.date === dateStr);
                          const timeWindow = unclaimedSignup?.timeWindow || formData.defaultTimeWindow;
                          
                          return (
                            <Box key={`date-${index}`} p={2} borderWidth="1px" borderRadius="md" bg="gray.50">
                              <HStack justify="space-between">
                                <VStack align="start" spacing={0}>
                                  <Text>Day {index + 1}: {formatDate(dateStr)}</Text>
                                  <Text fontSize="xs" color="gray.600">
                                    Meal drop off {formatTime(timeWindow?.start)} - {formatTime(timeWindow?.end)}
                                  </Text>
                                </VStack>
                                <Badge colorScheme="yellow">No one signed up yet</Badge>
                              </HStack>
                            </Box>
                          );
                        }
                      })}
                    </VStack>
                  ) : (
                    <Text>No delivery dates have been set.</Text>
                  )}
                </Box>
              </Box>
            )}
            
            {!isStatusActive && (
              <Box flex="1" alignSelf="center"  >
                <Button 
                  colorScheme="green" 
                  size="lg"
                  width="100%"
                  maxW="200px"  
                  alignSelf="center" 
                  onClick={async (e) => {
                    e.preventDefault();
                    
                    // Validate the form first
                    if (!isFormValid()) {
                      return;
                    }
                    
                    try {
                      // Save the form data first
                      await handleSubmit(e as unknown as React.FormEvent<Element>, false);
                      
                      // Only navigate to activation page if save was successful
                      navigate(`/registry/${registry.registryId}/activate/${registry.activationId}`);
                    } catch (error) {
                      console.error('Error saving before activation:', error);
                      // Don't navigate if there was an error
                    }
                  }}
                  isLoading={isSubmitting}
                  loadingText="Processing..."
                  _hover={{}}
                >
                  Activate MommaFuel
                </Button>
              </Box>
            )}
          </HStack>
          
          {successMessage && (
            <Alert status="success" borderRadius="md">
              <AlertIcon />
              {successMessage}
            </Alert>
          )}
          
          {error && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              {error}
            </Alert>
          )}
        </VStack>
      </Box>
      
      {/* MommaFuel Information Section - following Register page order */}
      <Box p={6}>        
        <VStack spacing={6} align="stretch" divider={<Box borderBottomWidth="1px" />}>

          {registry.status === 'PENDING' && (
            <Box>
              <Heading size="xs" mb={3} color="gray.500">Where would you like your meals delivered?</Heading>
              
              {/* Only show address visibility acknowledgment if address is provided */}
              {!noAddressProvided && (
                <FormControl isRequired mb={4}>
                  <Checkbox
                    isChecked={formData.addressVisibilityAcknowledged || false}
                    onChange={(e) => setFormData((prev: typeof formData) => ({
                      ...prev,
                      addressVisibilityAcknowledged: e.target.checked
                    }))}
                    colorScheme="green"
                    sx={{
                      '& .chakra-checkbox__control': {
                        borderColor: 'gray.800',
                        borderWidth: '2px'
                      }
                    }}
                  >
                    <Text fontSize="md">
                      I understand: anyone with the MommaFuel link will be able to see this delivery address
                    </Text>
                  </Checkbox>
                </FormControl>
              )}

              {/* Add checkbox for opting out of providing address - always visible */}
              <FormControl mb={4}>
                <Checkbox
                  isChecked={noAddressProvided}
                  onChange={(e) => handleNoAddressChange(e.target.checked)}
                  colorScheme="green"
                  sx={{
                    '& .chakra-checkbox__control': {
                      borderColor: 'gray.800',
                      borderWidth: '2px'
                    }
                  }}
                >
                  <Text fontSize="md">
                    I'd prefer not to provide an address
                  </Text>
                </Checkbox>
              </FormControl>

              <VStack align="stretch" spacing={4}>
                <Flex direction={{ base: "column", md: "row" }} width="100%" gap="4">
                  <FormControl isRequired={!noAddressProvided}>
                    <FormLabel color={noAddressProvided ? "gray.400" : "inherit"}>Street Address</FormLabel>
                    <Input 
                      value={formData.address?.street || ''}
                      onChange={(e) => handleNestedChange('address', 'street', e.target.value)}
                      isDisabled={noAddressProvided}
                      bg={noAddressProvided ? "gray.100" : "white"}
                    />
                  </FormControl>
                  
                  <FormControl>
                    <FormLabel color={noAddressProvided ? "gray.400" : "inherit"}>Apartment, Suite, etc.</FormLabel>
                    <Input 
                      value={formData.address?.street2 || ''}
                      onChange={(e) => handleNestedChange('address', 'street2', e.target.value)}
                      isDisabled={noAddressProvided}
                      bg={noAddressProvided ? "gray.100" : "white"}
                    />
                  </FormControl>
                </Flex>

                <Flex direction={{ base: "column", md: "row" }} width="100%" gap="4">
                  <FormControl isRequired={!noAddressProvided}>
                    <FormLabel color={noAddressProvided ? "gray.400" : "inherit"}>City</FormLabel>
                    <Input 
                      value={formData.address?.city || ''}
                      onChange={(e) => handleNestedChange('address', 'city', e.target.value)}
                      placeholder=""
                      isDisabled={noAddressProvided}
                      bg={noAddressProvided ? "gray.100" : "white"}
                    />
                  </FormControl>
                  
                  <FormControl isRequired={!noAddressProvided}>
                    <FormLabel color={noAddressProvided ? "gray.400" : "inherit"}>State</FormLabel>
                    <Select
                      name="address.state"
                      value={formData.address?.state || ''}
                      onChange={(e) => handleNestedChange('address', 'state', e.target.value)}
                      placeholder="--"
                      borderColor="black"
                      borderWidth="1px"
                      isDisabled={noAddressProvided}
                      bg={noAddressProvided ? "gray.100" : "white"}
                    >
                      {usStates.map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </Select>
                  </FormControl>
                  
                  <FormControl isRequired={!noAddressProvided}>
                    <FormLabel color={noAddressProvided ? "gray.400" : "inherit"}>ZIP Code</FormLabel>
                    <Input 
                      value={formData.address?.zip || ''}
                      onChange={(e) => handleNestedChange('address', 'zip', e.target.value)}
                      onKeyDown={SharingUtils.onlyAllowDigits}
                      isDisabled={noAddressProvided}
                      bg={noAddressProvided ? "gray.100" : "white"}
                    />
                  </FormControl>
                </Flex>
              </VStack>
            </Box>
          )}

          {registry.status === 'PENDING' && (
            <Box>
              <Heading size="xs" mb={3} color="gray.500">Meal Specifics</Heading>
              <VStack align="stretch" spacing={4}>
              <Stack direction={{ base: "column", md: "row" }} spacing={4} align={{ base: "stretch", md: "flex-start" }} width="100%">
                  {
                    registry.status === 'PENDING' && (
                      <FormControl isRequired flex={1}>
                        <Text fontWeight="medium">How many days of meals?</Text>
                    <Select 
                      name="mealDays"
                      borderColor="black"
                      borderWidth="1px"
                      value={formData.mealDays?.toString() || "5"}
                      onChange={(e) => {
                        // Store the number of meal days in the form data
                        setFormData((prev: typeof formData) => ({
                          ...prev,
                          mealDays: parseInt(e.target.value, 10)
                        }));
                      }}
                    >
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21].map(days => {
                        // If registry is active, only show options >= current meal days
                        if (registry.status === 'ACTIVE' && days < registry.mealDays) {
                          return null;
                        }
                        return (
                          <option key={days} value={days.toString()}>
                            {days} {days === 1 ? 'day' : 'days'}
                          </option>
                        );
                      })}
                    </Select>
                  </FormControl>
                  )}
                  <FormControl isRequired flex={1} minWidth="225px">
                    <Text fontWeight="medium">How many adults are eating?</Text>
                    <Select
                      name="numberOfAdults"
                      borderColor="black"
                      borderWidth="1px"
                      value={formData.numberOfAdults?.toString() || "2"}
                      onChange={(e) => {
                        // Store the number of adults in the form data
                        setFormData((prev: typeof formData) => ({
                          ...prev,
                          numberOfAdults: parseInt(e.target.value, 10),
                          // Update total number of people for backward compatibility
                          numberOfPeople: parseInt(e.target.value, 10) + (prev.numberOfKids || 0)
                        }));
                      }}
                    >
                      <option value="1">1 adult</option>
                      <option value="2">2 adults</option>
                      <option value="3">3 adults</option>
                      <option value="4">4 adults</option>
                      <option value="5">5 adults</option>
                      <option value="6">6 adults</option>
                      <option value="7">7 adults</option>
                      <option value="8">8+ adults</option>
                    </Select>
                  </FormControl>
                  
                  <FormControl isRequired flex={1} minWidth="225px">
                    <Text fontWeight="medium">How many children are eating?</Text>
                    <Select
                      name="numberOfKids"
                      borderColor="black"
                      borderWidth="1px"
                      value={formData.numberOfKids?.toString() || "0"}
                      onChange={(e) => {
                        // Store the number of children in the form data
                        setFormData((prev: typeof formData) => ({
                          ...prev,
                          numberOfKids: parseInt(e.target.value, 10),
                          // Update total number of people for backward compatibility
                          numberOfPeople: (prev.numberOfAdults || 2) + parseInt(e.target.value, 10)
                        }));
                      }}
                    >
                      <option value="0">0 children</option>
                      <option value="1">1 child</option>
                      <option value="2">2 children</option>
                      <option value="3">3 children</option>
                      <option value="4">4 children</option>
                      <option value="5">5 children</option>
                      <option value="6">6 children</option>
                      <option value="7">7+ children</option>
                    </Select>
                  </FormControl>
                </Stack>
              </VStack>
            </Box>
          )}

          {registry.status === 'PENDING' && (
            <Box>
              <Heading size="xs" mb={3} color="gray.500">What time window should meals be delivered?</Heading>
              <VStack align="stretch" spacing={4}>
                <Stack direction={{ base: "column", md: "row" }} spacing={4} align={{ base: "stretch", md: "flex-start" }} width="100%">
                  <FormControl isRequired flex={1}>
                    <Text fontWeight="medium" align="start" mb={2}>Start Time</Text>
                    <Input 
                      type="time"
                      value={formData.defaultTimeWindow?.start || '17:00'}
                      onChange={(e) => handleNestedChange('defaultTimeWindow', 'start', e.target.value)}
                    />
                  </FormControl>
                  
                  <FormControl isRequired flex={1}>
                    <Text fontWeight="medium" align="start" mb={2}>End Time</Text>
                    <Input 
                      type="time"
                      value={formData.defaultTimeWindow?.end || '19:00'}
                      onChange={(e) => handleNestedChange('defaultTimeWindow', 'end', e.target.value)}
                    />
                    {formData.defaultTimeWindow?.end <= formData.defaultTimeWindow?.start && (
                      <Text color="red.500" fontSize="xs" mt="1">
                        End time must be after start time
                      </Text>
                    )}
                  </FormControl>
                </Stack>
              </VStack>
            </Box>
          )}
          
          {/* Additional Notes */}  
          <FormControl >
            <FormLabel>Additional Delivery Instructions</FormLabel>
              <Textarea 
                name="additionalNotes"
                value={formData.additionalNotes || ''}
                onChange={handleInputChange}
                placeholder="Doorbell doesn't work, please text when arriving, etc."
                rows={4}
              />
          </FormControl>

          {/* Food Preferences */}
          <Box>
            <Heading size="xs" mb={3} color="gray.500">Meal Preferences</Heading>
            <Text fontSize="sm" mb={4}> Your friends will deliver a dinner of their choosing!  However, if you'd like to add suggestions - click here!</Text>

            {/* Show selected preferences count */}
            {formData.mealTags && formData.mealTags.length > 0 && (
              <Box 
                p={4} 
                borderWidth="1px" 
                borderRadius="md" 
                width="100%"
                mb={4}
              >

                <SimpleGrid spacing="0" columns={{ base: 2, md: 3, lg: 4}}>
                  {formData.mealTags.map((tag: { name: string }, i: number) => (
                    <Text key={i} fontSize="sm" fontWeight="medium">
                      {mealTags.find((mealTag: { name: string; icon?: string }) => mealTag.name === tag.name)?.icon || "🏷️"} {tag.name}
                    </Text>
                  ))}
                </SimpleGrid>
              </Box>
            )}

            <Button
              bg="mamaFuel.moss.700"
              color="white"
              _hover={{ bg: "mamaFuel.moss.600" }}
              size="lg"
              onClick={() => setIsMealPreferencesOpen(true)}
            >
              {formData.mealTags && formData.mealTags.length > 0 ? 'Edit Meal Suggestions' : 'Add Meal Suggestions'}
            </Button>

            {/* Meal Preferences Dialog */}
            <MealPreferencesDialog
              isOpen={isMealPreferencesOpen}
              onClose={() => setIsMealPreferencesOpen(false)}
              formData={formData}
              setFormData={setFormData}
            />


            {/* Food Dislikes */}
            <Box>
              <FormControl mb={4} mt={4}>
                <FormLabel>Foods you'd prefer not to receive</FormLabel>
                <Textarea 
                  name="dislikes"
                  value={formData.dislikes ? formData.dislikes.join(', ') : ''}
                  onChange={(e) => {
                    const dislikesText = e.target.value;
                    // Store text as-is in a single-element array
                    setFormData((prev: typeof formData) => ({
                      ...prev,
                      dislikes: dislikesText ? [dislikesText] : []
                    }));
                  }}
                  rows={3}
                />
              </FormControl>
              
              {/* Dietary Restrictions */}
              <FormControl mt={4}>
                <FormLabel>Dietary Restrictions or Special Needs</FormLabel>
                <Textarea 
                  name="dietaryRestrictions"
                  value={formData.dietaryRestrictions || ''}
                  onChange={handleInputChange}
                  placeholder="Vegetarian, vegan, gluten-free, etc."
                  rows={3}
                />
              </FormControl>
            </Box>
            
            {/* Submit Buttons */}
            <Stack 
              mt={6} 
              spacing={4} 
              direction={{ base: "column", md: "row" }} 
              justify={{ base: "stretch", md: "space-between" }}
              align={{ base: "stretch", md: "center" }}
            >
              <Button
                colorScheme="red"
                variant="red"
                onClick={() => setShowDeleteConfirm(true)}
              >
                Delete MommaFuel
              </Button>
              
              <Button
                colorScheme="blue"
                type="submit"
                isLoading={isSubmitting}
                loadingText="Saving Changes"
              >
                Save Changes and Exit
              </Button>
            </Stack>
            
            {/* Delete Confirmation Dialog */}
            {showDeleteConfirm && (
              <Box
                position="fixed"
                top="0"
                left="0"
                right="0"
                bottom="0"
                bg="rgba(0,0,0,0.7)"
                zIndex="1000"
                display="flex"
                alignItems="center"
                justifyContent="center"
                p={4}
              >
                <Box
                  bg="white"
                  p={6}
                  borderRadius="md"
                  maxW="md"
                  width="100%"
                >
                  <Heading size="md" mb={4}>Delete MommaFuel</Heading>
                  <Text mb={6}>
                    Are you sure you want to permanently delete this MommaFuel? This action cannot be undone.
                  </Text>
                  <HStack spacing={4} justify="space-between">
                    <Button 
                      variant="outline" 
                      onClick={() => setShowDeleteConfirm(false)}
                    >
                      Cancel
                    </Button>
                    <Button 
                      variant="red"
                      onClick={handleDeleteRegistry}
                      isLoading={isDeleting}
                      loadingText="Deleting..."
                    >
                      Yes, Delete MommaFuel
                    </Button>
                  </HStack>
                </Box>
              </Box>
            )}
          </Box>
        </VStack>
      </Box>
      
      {/* Add the MealScheduleCustomizationDialog */}
      <MealScheduleCustomizationDialog
        isOpen={isMealScheduleOpen}
        onClose={() => setIsMealScheduleOpen(false)}
        onSave={handleSaveMealSchedule}
        defaultDates={formData.currentSignUps?.map((signup: any) => signup.date) || []}
        defaultTimeWindow={formData.defaultTimeWindow || { start: '17:00', end: '19:00' }}
        currentCustomSchedule={getCurrentMealSchedule()}
        claimedDates={getClaimedDates()}
      />

      {/* Add No Address Confirmation Dialog */}
      <AlertDialog
        isOpen={isNoAddressDialogOpen}
        leastDestructiveRef={cancelRef as React.RefObject<any>}
        onClose={onNoAddressDialogClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              No Address Confirmation
            </AlertDialogHeader>

            <AlertDialogBody>
              Check this box if you don't any delivery address to be shown. Only do this if everyone bringing meals already knows where to go!
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={handleCancelNoAddress}>
                Cancel
              </Button>
              <Button 
                colorScheme="blue" 
                onClick={handleConfirmNoAddress} 
                ml={3}
              >
                Confirm
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default RegistryView; 